import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import SEO from "@/components/SEO";
import DemoBooking from "@/components/DemoBooking";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from 'react-i18next';

const contactSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters").max(100),
  company: z.string().min(2, "Company name is required").max(100),
  email: z.string().email("Please enter a valid email address").max(255),
  phone: z.string().optional(),
  useCase: z.string().min(1, "Please select a use case"),
  message: z.string().min(10, "Message must be at least 10 characters").max(1000),
});

type ContactForm = z.infer<typeof contactSchema>;

const Contact = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBooking, setShowBooking] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactForm) => {
    setIsSubmitting(true);

    try {
      const response = await fetch("/contact-form.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: data.name,
          company: data.company,
          email: data.email,
          phone: data.phone || "",
          useCase: data.useCase,
          message: data.message,
          isBooking: false,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to send message");
      }

      toast({
        title: "Message Sent Successfully! ✉️",
        description: "We'll get back to you within 24 hours.",
      });
      reset();
    } catch (error) {
      console.error(error);
      toast({
        title: "Failed to Send",
        description:
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again or email us directly at info@Holionix.com",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <SEO
        title="Contact Holionix - Get in Touch with Our AI Voice Experts"
        description="Contact Holionix for enterprise AI voice solutions. Schedule a demo, get pricing, or speak with our sales team. Email, phone, and live chat support available."
      />
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-shadow-glow">
              {t('contact.title')}
            </h1>
            <p className="text-xl text-foreground/80 max-w-2xl mx-auto mb-8">
              {t('contact.subtitle')}
            </p>
            
            <div className="flex justify-center gap-4 mb-8">
              <Button
                onClick={() => setShowBooking(false)}
                variant={!showBooking ? "default" : "outline"}
                className={!showBooking ? "gradient-brand text-white" : "border-accent text-accent hover:bg-accent/10"}
              >
                {t('contact_info.send_message')}
              </Button>
              <Button
                onClick={() => setShowBooking(true)}
                variant={showBooking ? "default" : "outline"}
                className={showBooking ? "gradient-brand text-white" : "border-accent text-accent hover:bg-accent/10"}
              >
                {t('contact_info.book_demo')}
              </Button>
            </div>
          </motion.div>

          {showBooking ? (
            <DemoBooking />
          ) : (
            <div className="grid lg:grid-cols-2 gap-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="card-dark gradient-border p-8">
                  <h2 className="text-2xl font-bold mb-6">{t('contact.form_title')}</h2>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                      <Label htmlFor="name">{t('contact.label_name')} *</Label>
                      <Input
                        id="name"
                        {...register("name")}
                        placeholder={t('contact.placeholder_name')}
                        className="mt-1"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="company">{t('contact.label_company')} *</Label>
                      <Input
                        id="company"
                        {...register("company")}
                        placeholder={t('contact.placeholder_company')}
                        className="mt-1"
                      />
                      {errors.company && (
                        <p className="text-red-500 text-sm mt-1">{errors.company.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="email">{t('contact.label_email')} *</Label>
                      <Input
                        id="email"
                        type="email"
                        {...register("email")}
                        placeholder={t('contact.placeholder_email')}
                        className="mt-1"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="phone">{t('contact.label_phone')}</Label>
                      <Input
                        id="phone"
                        type="tel"
                        {...register("phone")}
                        placeholder={t('contact.placeholder_phone')}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="useCase">{t('contact.label_usecase')} *</Label>
                      <select
                        id="useCase"
                        {...register("useCase")}
                        className="w-full mt-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-accent focus:ring-2 focus:ring-accent/20"
                      >
                        <option value="">{t('contact.placeholder_usecase')}</option>
                        <option value="inbound-support">{t('contact.usecase_inbound')}</option>
                        <option value="outbound-sales">{t('contact.usecase_outbound_sales')}</option>
                        <option value="appointment-booking">Appointment Booking</option>
                        <option value="survey-feedback">Surveys & Feedback</option>
                        <option value="other">{t('contact.usecase_other')}</option>
                      </select>
                      {errors.useCase && (
                        <p className="text-red-500 text-sm mt-1">{errors.useCase.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="message">{t('contact.label_message')} *</Label>
                      <Textarea
                        id="message"
                        {...register("message")}
                        placeholder={t('contact.placeholder_message')}
                        rows={5}
                        className="mt-1"
                      />
                      {errors.message && (
                        <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      className="w-full gradient-brand text-white hover:opacity-90 h-12 text-base"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          {t('contact.submit_button')}
                        </>
                      )}
                    </Button>
                  </form>

                  
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="space-y-6"
              >
                <Card className="card-dark gradient-border p-8 bg-gradient-to-br from-gradient-pink/10 to-gradient-purple/10">
                  <h2 className="text-2xl font-bold mb-6">{t('contact_info.title')}</h2>
                  
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg gradient-brand flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold mb-1">{t('contact_info.email_label')}</p>
                        <a href="mailto:info@Holionix.com" className="text-accent hover:underline">
                          info@Holionix.com
                        </a>
                        <p className="text-sm text-foreground/60 mt-1">{t('contact_info.email_desc')}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg gradient-brand flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold mb-1">{t('contact_info.phone_label')}</p>
                        <a href="tel:+49-15216724688" className="text-accent hover:underline">
                          +49 15216724688
                        </a>
                        <p className="text-sm text-foreground/60 mt-1">{t('contact_info.phone_hours')}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg gradient-brand flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold mb-1">{t('contact_info.headquarters_label')}</p>
                        <p className="text-foreground/70">
                          Holionix GmbH<br />
                          Heinrich-Pesch-Straße 3<br />
                          50739 Köln, Germany
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg gradient-brand flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold mb-1">{t('contact_info.response_time_label')}</p>
                        <p className="text-foreground/70">
                          {t('contact_info.response_time_desc')}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark gradient-border p-8 text-center glow-effect">
                  <h3 className="text-xl font-bold mb-4">{t('contact_info.partnerships_title')}</h3>
                  <p className="text-foreground/70 mb-4">
                    {t('contact_info.partnerships_desc')}
                  </p>
                  <Button variant="outline" className="border-accent text-accent hover:bg-accent/10">
                    {t('contact_info.partnerships_button')}
                  </Button>
                </Card>
              </motion.div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
